import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/Button";
import { z } from "zod";
import { FormCard } from "@/components/FormCard";
import { useAuth } from "@/hooks/useAuth";
import { useSearchParams } from "react-router";

const sendVerificationSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }).trim(),
});

type TSendVerificationSchema = z.infer<typeof sendVerificationSchema>;

type SendVerificationFormProps = {
  handleEmailSent: (email: string) => void;
  handleVerified: () => void;
};

export default function SendVerificationForm({
  handleEmailSent,
  handleVerified,
}: SendVerificationFormProps) {
  const { handleSendVerification } = useAuth();
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get("email") || "";
  const redirectQuery = searchParams.get("redirect") || "";
  const isRedirectingToReset = redirectQuery.startsWith("/reset-password");

  const form = useForm<TSendVerificationSchema>({
    resolver: zodResolver(sendVerificationSchema),
    defaultValues: {
      email: emailFromQuery,
    },
  });

  const onSubmit = async (formData: TSendVerificationSchema) => {
    const result = await handleSendVerification(formData.email, false);
    if (result.success) {
      handleEmailSent(formData.email);
    } else if (result.alreadyVerified) {
      handleVerified();
    } else {
      form.setError("email", {
        type: "manual",
        message: "Something went wrong",
      });
    }
  };

  const description = isRedirectingToReset
    ? "You need to verify your email before resetting your password."
    : "Enter your email to receive a verification code";

  return (
    <FormCard
      form={form}
      onSubmit={onSubmit}
      title="Verify your email"
      description={description}
    >
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input placeholder="you@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <Button
        type="submit"
        className="w-full"
        disabled={form.formState.isSubmitting}
      >
        {form.formState.isSubmitting ? "Sending..." : "Send Verification Code"}
      </Button>
    </FormCard>
  );
}
